import React from 'react';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { PageTypeTitle } from '../../../_lib/components/PageType';
import { PageType } from '../../../types/api/PageType';
import { BreadcrumbFlyoutProps } from './BreadcrumbFlyout';
import { ExtendedInfoStateIndicator, ExtendedInfoSVG } from '../../../_lib/components/ExtendedInfo';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Siblings } from './Siblings';
import { NavItem } from '../../../types/api/NavItem';

export type BreadcrumbItemProps = {
  navItem?: NavItem & { pageTypeResource?: PageType };
  siblings?: BreadcrumbFlyoutProps;
  index: number;
};

export function BreadcrumbItem({ siblings = undefined, navItem = undefined, index }: BreadcrumbItemProps) {
  const { t } = useSonTranslation('Breadcrumb');
  if (!navItem) {
    return null;
  }
  const getSiblings = () => {
    if (siblings && siblings.groups.length > 0) {
      return <Siblings navItem={navItem} siblings={siblings} index={index} />;
    }
    return null;
  };

  const getHasReadRestrictionsIcon = () => {
    if (navItem.hasReadRestrictions) {
      return (
        <ExtendedInfoSVG
          helpText={t('readRestrictionDescriptionText')}
          tooltipPlacement={TooltipPlacement.TOP}
          svgSpriteID={Icons.CLOSED_LOCK}
          modifier={['nomargin-left', 'margin-right']}
        />
      );
    }
    return null;
  };

  const getSlashIcon = () => {
    return (
      <span className="breadcrumb__iconcontainer">
        <SVG svgSpriteId={Icons.BREADCRUMB_SLASH} />
      </span>
    );
  };

  const getPageStateIndicator = () => {
    if (navItem.packState === 'onlyDraftVersion') {
      return (
        <span className="breadcrumb__extended-info">
          <ExtendedInfoStateIndicator tooltipPlacement={TooltipPlacement.TOP} stateIndicatorStatus="red" />
        </span>
      );
    }
    return null;
  };

  const getTitleAttribute = () => {
    // expose title only, when it differs from link name
    if (navItem.link.name !== navItem.title) {
      return navItem.title;
    }
    return null;
  };

  /**
   * Single Icon with no link.
   * Only visible on the Homepage
   */
  const renderItemStaticInstitution = () => {
    return (
      <>
        <span className="breadcrumb__listlink breadcrumb__listlink--homepage breadcrumb__listlink--homepage-nolink">
          <SVG svgSpriteId={Icons.HOME_HOUSE} />
        </span>
        {/* this is a "disabled link", this is the proper markup for that */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="breadcrumb__location breadcrumb__location--homepage"
          role="link"
          aria-disabled="true"
          aria-current="page"
        >
          {navItem.link.name}
        </a>
      </>
    );
  };

  /**
   * First item in a breadcrumb list linking to the homepage.
   */
  const renderItemLinkedInstitution = () => {
    return (
      <Hyperlink
        tooltipPlacement={TooltipPlacement.TOP}
        hideTooltipFromScreenReader
        to={navItem.link.url}
        className="breadcrumb__listlink breadcrumb__listlink--homepage"
        title={navItem.link.name}
      >
        <span className="invisible">{navItem.link.name}</span>
        <SVG svgSpriteId={Icons.HOME_HOUSE} />
      </Hyperlink>
    );
  };

  /**
   * Rendering a breadcrumbitem linking to a parent page.
   * previous = direct parent, navigation needs rel="prev"
   * regular = a page higher in the tree, not direct sibling
   */
  const renderItemPreviousOrRegular = (isRegular: boolean) => {
    return (
      <>
        {getSlashIcon()}
        {getPageStateIndicator()}
        <Hyperlink
          tooltipPlacement={TooltipPlacement.TOP}
          hideTooltipFromScreenReader
          to={navItem.link.url}
          className="breadcrumb__listlink"
          rel={isRegular ? null : 'prev'}
          title={getTitleAttribute()}
        >
          {navItem.link.name}
        </Hyperlink>
        {getHasReadRestrictionsIcon()}
        {getSiblings()}
      </>
    );
  };

  const getItemName = () => {
    return navItem.pageTypeResource ? <PageTypeTitle pageType={navItem.pageTypeResource} /> : navItem.link.name;
  };

  /**
   * Item for the current page which is not linked.
   */
  const renderItemCurrent = () => {
    return (
      <>
        {getSlashIcon()}
        {getPageStateIndicator()}
        {/* this is a "disabled link", this is the proper markup for that */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className="breadcrumb__location" role="link" aria-disabled="true" aria-current="page">
          {getItemName()}
        </a>
        {getHasReadRestrictionsIcon()}
        {getSiblings()}
      </>
    );
  };

  const renderBreadCrumbItemType = (position: string[]) => {
    if (position.includes('first') && position.includes('self')) {
      return renderItemStaticInstitution();
    }

    if (position.includes('first')) {
      return renderItemLinkedInstitution();
    }

    if (position.includes('previous') || position.length === 0) {
      return renderItemPreviousOrRegular(position.length === 0);
    }

    if (position.includes('self')) {
      return renderItemCurrent();
    }
  };

  return <li className="breadcrumb__listelement">{renderBreadCrumbItemType(navItem.position)}</li>;
}
